import type { RuleSet } from "./types";
import { RULE_STEPS } from "./constants";
import { protectedRange, WEEKDAY_NAMES, WEEKDAYS } from "./protected-hours";
import { formatDuration } from "../lib/duration";

const on = (steps: readonly number[], v: number) => steps.includes(v);

/**
 * Owner-facing reasons a rule set is off the fixed steps; empty when it is on them. Expects a rule
 * set `validateRuleSet` already accepted. Only `PUT /api/rules` and the Rules page call this, never
 * the loader, so a stored off-step set keeps planning. The cleaning / setup limit is not checked.
 */
export function checkRuleSteps(r: RuleSet): string[] {
  const errors: string[] = [];
  for (const t of r.tiers) {
    if (!on(RULE_STEPS.blockMinutes, t.blockMinutes)) errors.push(`The ${formatDuration(t.blockMinutes)} BTB block is not one of the standard lengths (30 min, 45 min, 1 hour, then every half hour up to 3 hours)`);
  }
  for (const t of r.tiers) {
    if (!on(RULE_STEPS.tierStartMinutes, t.minGap)) errors.push(`A rule starting at ${formatDuration(t.minGap)} open is not on a half hour between 30 min and 4 hours`);
  }
  if (!on(RULE_STEPS.removalUtilizationPct, r.removalUtilizationPct)) errors.push("Pick a booked percentage from 30% to 90%, in steps of 10");
  if (!on(RULE_STEPS.removalMinGapMinutes, r.removalMinGapMinutes)) errors.push("Pick 30 min, 45 min, 1 hour, 1.5 hours or 2 hours for the open time next to a block");
  if (!on(RULE_STEPS.minShiftMinutes, r.minShiftMinutes)) errors.push("Pick a shortest shift of 2 to 6 hours, in whole hours");
  if (!on(RULE_STEPS.lookaheadDays, r.lookaheadDays)) errors.push("Pick 1 week, 10 days, 2 weeks, 3 weeks or 4 weeks for how far ahead to plan");
  if (r.startLockHoursBefore !== null && !on(RULE_STEPS.startLockHoursBefore, r.startLockHoursBefore)) errors.push("Pick 2, 4, 6, 8, 10, 12, 14, 16, 18 or 24 hours before the shift starts for the start lock");
  if (r.endLockHoursBefore !== null && !on(RULE_STEPS.endLockHoursBefore, r.endLockHoursBefore)) errors.push("Pick 2, 4, 6, 8, 10, 12, 14, 16, 18 or 24 hours before the shift ends for the end lock");
  for (const d of WEEKDAYS) {
    const range = protectedRange(r.protectedHours[d]);
    if (range && (range.from % RULE_STEPS.protectedHoursStepMinutes !== 0 || range.to % RULE_STEPS.protectedHoursStepMinutes !== 0)) {
      errors.push(`Protected hours on ${WEEKDAY_NAMES[d]} must start and end on the hour or half hour`);
    }
  }
  return errors;
}
